/* eslint-disable import/no-anonymous-default-export */

// this is a pluggable snippet for basic reflection functions, plucked it separate for organization

import DiffuseSampler from "./texture-samplers/diffuse_sampler.glsl";
export default /* glsl */`

    vec3 worldNormal = inverseTransformDirection( normalize( normal ), viewMatrix );
    vec3 worldViewDir = inverseTransformDirection( -viewDir, viewMatrix ); // view dir is from surface to camera, invert it
    vec3 worldTangent = inverseTransformDirection( normalize( vAnisotropicTangent ), viewMatrix );
    vec3 worldBitangent = cross(worldNormal, worldTangent);

    // create a new reflect vector since we don't want to reuse the old stuff
    vec3 anistropizedNormal = getAnisotropizedNormal(worldNormal, worldTangent, worldViewDir, anisotropyStrength);
    reflectVec = reflect( -viewDir, normal );
    reflectVec = inverseTransformDirection( reflectVec, viewMatrix );

	// Mixing the reflection with the normal is more accurate and keeps rough objects from gathering light from behind their tangent plane.
	reflectVec = normalize( mix( reflectVec, normal, roughness * roughness) );
    if (useBoxProjection) {
        queryReflectVec = parallaxCorrectedNormal( reflectVec, boxProjectionSize, boxProjectionPosition, vWorldPosition);
    } else {
        queryReflectVec = vec3( flipEnvMap * reflectVec.x, reflectVec.yz );
    }

    vec3 adjustedBoxSize = boxProjectionSize; // tried to see if there is any sense in adjusting the box
    // figure out the reflection point
    vec3 adjustmentScale = 1.0 / adjustedBoxSize;
    vec3 adjustedVector = queryReflectVec * adjustmentScale;


    // testing if we can calculate the distance between points by using the uvw difference
    vec3 previousAdjustedVector = adjustedVector;
    vec3 cubeAdjustedStartingPosition = getNormalizedPosition(adjustedBoxSize, boxProjectionPosition, vWorldPosition) * normalize(adjustmentScale);

    vec4 ormSample;
    vec4 normalSample;
    vec4 envMapSample;
    vec4 accumulation;
    vec3 nextReflectionVector = reflectVec;

    vec4 debugColor1 = vec4(1.0, 0.0, 0.0, 1.0);
    vec4 debugColor2 = vec4(0.0, 1.0, 0.0, 1.0);

    float energyLeft = 1.0;
    float smoothnessLeft = 1.0 - (roughness * 1.5) + 0.05; // no idea why this magic number is needed to hit 0.0
    vec3 colorLeft = vec3(1.0);
    int bouncesDone = 0;
    
    // first hit has travelled from v position to adjusted vector?,
    float distanceTravelled = distance(cubeAdjustedStartingPosition, adjustedVector); // bias the distance a bit

    vec4 diffuseColor = vec4(0.0);
    diffuseColor.rgb = diffuse;
    
    ${DiffuseSampler}
    
    float adjustedSpecularLevel = 0.0; // for debug testing
    float dotProduct = 0.0;
    float occlusionFactor = 1.0;
    float roughnessFactor = roughness; // roughen up when there is no energy
    float metalnessFactor = metalness;


    while(energyLeft > 0.0){
        // a hard cap for reflections
        if (bouncesDone >= reflectionBounces){
            break;
        }
        if (bouncesDone == 1){
            diffuseColor *= 0.75; // a quick and stupid hack to even out some color mess
        }

        float specularLevel = 1.0 - smoothnessLeft;
        adjustedSpecularLevel = mix(specularLevel / 3.0, specularLevel, distanceTravelled); // if we haven't travelled far, we can use a sharper version
        
        float specularMipLevel = getSpecularMIPLevel(adjustedSpecularLevel, maxMIPLevel);
        float normalizedMipLevel = specularMipLevel / float(maxMIPLevel);
        queryReflectVec = parallaxCorrectedNormal( nextReflectionVector, boxProjectionSize, vec3(0.0), queryReflectVec);
        adjustedVector = queryReflectVec * adjustmentScale;

        //adjustedVector = mix(adjustedVector, worldTangent, 1.0 - magic1);

        envMapSample = textureCubeLodEXT(envMap, adjustedVector, specularMipLevel);

        ormSample = textureCubeLodEXT( ormCubemap, adjustedVector, specularMipLevel);
        
        normalSample = textureCube( normalCubemap, adjustedVector) * 2.0 - 1.0;
        vec4 defaultNormalSample = textureCube( defaultNormalCubemap, adjustedVector) * 2.0 - 1.0;
        vec3 adjustedNormalSample = mix(normalSample.xyz, defaultNormalSample.xyz, normalizedMipLevel);

        nextReflectionVector = reflect( nextReflectionVector, adjustedNormalSample );
        roughnessFactor = max(ormSample.y, roughnessFactor); // we don't actually want to make the reflection too glossy if there is a mirror behind it
        metalnessFactor = ormSample.z;

        // this fixes steel roughness
        //roughnessFactor = 1.0 - roughnessFactor;
        //roughnessFactor += ormSample.y * 0.5;

        float mirrorness = max(metalnessFactor - roughnessFactor, 0.0);
        float adjustmentFactor = 1.0;

        // contribute less with high mirrorness
        // TODO: find out a bit better name for this one
        adjustmentFactor = 1.0 - mirrorness;

        vec3 finalSample = envMapSample.rgb * colorLeft * adjustmentFactor;

        // basically move the graph so that it starts to affect only from 0.5 onwards
        float mixFactor = max(1.0 - adjustedSpecularLevel * 2.0, 0.0);
        diffuseColor.rgb = mix(diffuseColor.rgb, finalSample, mixFactor);

        //diffuseSample = desaturate(diffuseColor.rgb, (1.0 - energyLeft)); // remove saturation buildup, but not completely

        accumulation.rgb = mix(accumulation.rgb, diffuseColor.rgb, energyLeft);

        float lossFactor = 1.0 - (roughnessFactor * (1.0 - mixFactor));
        energyLeft *= lossFactor;
        previousAdjustedVector = adjustedVector;
        colorLeft *= envMapSample.rgb;
        float smoothnessLoss = adjustmentFactor + adjustedSpecularLevel;
        smoothnessLeft = max(0.0, smoothnessLeft - smoothnessLoss); // maybe not neccessary
        distanceTravelled += distance(previousAdjustedVector, adjustedVector);
        bouncesDone++;
    }

    if (reflectionDebug){
        // remove artefacts
        //envMapColor.rgb = getAnisotropizedNormal(worldNormal, worldTangent, worldViewDir, anisotropyStrength);
        vec3 lightDir = normalize(vec3(1.0, -1.0, 0.0));
        envMapColor.rgb = getLightAnisotropyNormal(lightDir, worldNormal, worldTangent, worldViewDir, anisotropyStrength);
        //envMapColor.rgb = normalize(queryReflectVec);
        //envMapColor.rgb = worldTangent;
        //envMapColor.rgb = worldBitangent;
        envMapColor.a = 1.0;
    } else {
        envMapColor = accumulation;
    }

    
`;